import * as fabric from 'fabric';

/**
 * GradientConfig 객체인지 판별
 */
export const isGradientConfig = (value) => {
  return value !== null && typeof value === 'object' && 'colorStops' in value && Array.isArray(value.colorStops);
};

/**
 * fabric.Gradient 인스턴스인지 판별
 */
export const isFabricGradient = (value) => {
  return value instanceof fabric.Gradient;
};

/**
 * HEX → RGB 배열 (3-char hex 지원)
 */
export const hexToRgb = (hex) => {
  let cleaned = hex.replace('#', '');
  if (cleaned.length === 3) {
    cleaned = cleaned[0]+cleaned[0]+cleaned[1]+cleaned[1]+cleaned[2]+cleaned[2];
  }
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleaned);
  return result ? [parseInt(result[1],16), parseInt(result[2],16), parseInt(result[3],16)] : [0,0,0];
};

/**
 * HEX → RGB 객체 (ColorPickerModal UI용)
 */
export const hexToRgbObj = (hex) => { const [r,g,b] = hexToRgb(hex); return {r,g,b}; };

/**
 * RGB 배열 → HEX
 */
export const rgbToHex = (rgb) => {
  if (!rgb || !Array.isArray(rgb)) return '#000000';
  return '#' + rgb.map(x => Math.max(0,Math.min(255,Math.round(x))).toString(16).padStart(2,'0')).join('');
};

/**
 * RGB 개별값 → HEX (ColorPickerModal UI용)
 */
export const rgbValuesToHex = (r, g, b) => rgbToHex([r, g, b]);

/**
 * 파서 gradient_data → GradientConfig
 */
export const parserToGradientConfig = (gradientData) => ({
  type: gradientData.type,
  angle: gradientData.angle,
  colorStops: gradientData.color_stops.map(s => ({
    offset: s.offset, color: `#${s.color}`, opacity: s.opacity,
  })),
});

/**
 * GradientConfig → 파서 gradient_data
 */
export const gradientConfigToParser = (config) => ({
  type: config.type,
  angle: config.angle || 0,
  color_stops: config.colorStops.map(s => ({
    offset: s.offset, color: s.color.replace('#', ''), opacity: s.opacity,
  })),
});

/**
 * hex + opacity → rgba 문자열
 */
export const hexToRgba = (hex, opacity = 1) => {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * 색상 문자열에서 hex 추출
 */
export const parseColorToHex = (color) => {
  if (!color || color === 'transparent') return '#000000';
  if (color.startsWith('#')) return color.substring(0, 7);

  // rgba(r, g, b, a) 파싱
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1]).toString(16).padStart(2, '0');
    const g = parseInt(rgbaMatch[2]).toString(16).padStart(2, '0');
    const b = parseInt(rgbaMatch[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  return '#000000';
};

/**
 * 색상 문자열에서 opacity 추출
 */
export const parseColorOpacity = (color) => {
  if (!color || color === 'transparent') return 0;
  const rgbaMatch = color.match(/rgba\(\d+,\s*\d+,\s*\d+,\s*([\d.]+)\)/);
  if (rgbaMatch) return parseFloat(rgbaMatch[1]);
  return 1;
};

/**
 * 각도와 도형 크기로 gradient 시작/끝점 계산 (PPT/CSS 호환)
 * gradient 선이 도형 전체를 edge-to-edge로 커버하도록 확장
 * 0°=좌→우, 90°=상→하
 */
const angleToCoords = (angle, width, height) => {
  const rad = angle * (Math.PI / 180);
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);

  // gradient 선의 반길이: 도형의 모든 꼭짓점을 커버하는 최소 길이
  const halfLength = (Math.abs(width * dx) + Math.abs(height * dy)) / 2;

  const cx = width / 2;
  const cy = height / 2;

  return {
    x1: cx - halfLength * dx,
    y1: cy - halfLength * dy,
    x2: cx + halfLength * dx,
    y2: cy + halfLength * dy,
  };
};

/**
 * fabric coords에서 각도 역산 (0°=좌→우, 90°=상→하)
 */
const coordsToAngle = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  let angle = Math.atan2(dy, dx) * (180 / Math.PI);
  if (angle < 0) angle += 360;
  return Math.round(angle) % 360;
};

/**
 * GradientConfig → fabric.Gradient
 */
export const toFabricGradient = (config, width, height) => {
  const colorStops = config.colorStops.map((stop) => ({
    offset: stop.offset,
    color: stop.opacity < 1 ? hexToRgba(stop.color, stop.opacity) : stop.color,
  }));

  if (config.type === 'radial') {
    // 정규화된 좌표(0~1)로 원형 gradient 정의 후 gradientTransform으로 타원 변환
    // r2 = √2/2 → 꼭짓점(farthest-corner)까지 도달 (PPT/CSS 호환)
    return new fabric.Gradient({
      type: 'radial',
      coords: {
        x1: 0.5,
        y1: 0.5,
        r1: 0,
        x2: 0.5,
        y2: 0.5,
        r2: Math.SQRT2 / 2,
      },
      gradientTransform: [width, 0, 0, height, 0, 0],
      colorStops,
    });
  }

  // linear
  const { x1, y1, x2, y2 } = angleToCoords(config.angle ?? 0, width, height);
  return new fabric.Gradient({
    type: 'linear',
    coords: { x1, y1, x2, y2 },
    colorStops,
  });
};

/**
 * fabric.Gradient → GradientConfig
 */
export const fromFabricGradient = (gradient, width, height) => {
  const colorStops = (gradient.colorStops || []).map((stop) => ({
    offset: stop.offset,
    color: parseColorToHex(stop.color),
    opacity: parseColorOpacity(stop.color),
  }));

  if (gradient.type === 'radial') {
    return {
      type: 'radial',
      colorStops,
    };
  }

  // linear — coordsToAngle은 방향벡터(dx,dy)만 사용하므로 절대좌표 그대로 전달
  const c = gradient.coords;
  const angle = coordsToAngle(c.x1, c.y1, c.x2, c.y2);

  return {
    type: 'linear',
    angle,
    colorStops,
  };
};

/**
 * 텍스트 전용 그라데이션: 실제 텍스트 영역에 맞게 gradient 좌표 계산
 * textbox.width(프레임 너비) 대신 calcTextWidth()(실제 텍스트 너비)를 사용
 * 정렬(left/center/right)에 따른 오프셋도 반영
 */
export const toFabricTextGradient = (config, textbox) => {
  const tw = (typeof textbox.calcTextWidth === 'function') ? textbox.calcTextWidth() : textbox.width;
  const textWidth = Math.max(tw, 1);
  const textHeight = Math.max(textbox.height || 100, 1);
  const boxWidth = textbox.width;

  let offsetX = 0;
  if (textbox.textAlign === 'center') {
    offsetX = (boxWidth - textWidth) / 2;
  } else if (textbox.textAlign === 'right') {
    offsetX = boxWidth - textWidth;
  }

  const colorStops = config.colorStops.map((stop) => ({
    offset: stop.offset,
    color: stop.opacity < 1 ? hexToRgba(stop.color, stop.opacity) : stop.color,
  }));

  if (config.type === 'radial') {
    // 텍스트 영역에 맞는 타원형 radial gradient (offsetX로 정렬 반영)
    // r2 = √2/2 → 꼭짓점까지 도달
    return new fabric.Gradient({
      type: 'radial',
      coords: {
        x1: 0.5,
        y1: 0.5,
        r1: 0,
        x2: 0.5,
        y2: 0.5,
        r2: Math.SQRT2 / 2,
      },
      gradientTransform: [textWidth, 0, 0, textHeight, offsetX, 0],
      colorStops,
    });
  }

  const { x1, y1, x2, y2 } = angleToCoords(config.angle ?? 0, textWidth, textHeight);
  return new fabric.Gradient({
    type: 'linear',
    coords: {
      x1: offsetX + x1,
      y1: y1,
      x2: offsetX + x2,
      y2: y2,
    },
    colorStops,
  });
};

/**
 * fabric.Gradient → GradientConfig (텍스트 전용 역변환)
 * toFabricTextGradient의 역함수 — 동일한 textbox를 기준으로 복원
 */
export const fromFabricTextGradient = (gradient, textbox) => {
  const tw = (typeof textbox.calcTextWidth === 'function') ? textbox.calcTextWidth() : textbox.width;
  const textWidth = Math.max(tw, 1);
  const textHeight = Math.max(textbox.height || 100, 1);
  const boxWidth = textbox.width;

  let offsetX = 0;
  if (textbox.textAlign === 'center') {
    offsetX = (boxWidth - textWidth) / 2;
  } else if (textbox.textAlign === 'right') {
    offsetX = boxWidth - textWidth;
  }

  const colorStops = (gradient.colorStops || []).map((stop) => ({
    offset: stop.offset,
    color: parseColorToHex(stop.color),
    opacity: parseColorOpacity(stop.color),
  }));

  if (gradient.type === 'radial') {
    return { type: 'radial', colorStops };
  }

  // coordsToAngle은 방향벡터(dx,dy)만 사용 — offsetX는 x1,x2에 동일하게 적용되어 상쇄됨
  const c = gradient.coords;
  const angle = coordsToAngle(c.x1, c.y1, c.x2, c.y2);

  return { type: 'linear', angle, colorStops };
};

/**
 * 통합 변환: string 그대로, GradientConfig → fabric.Gradient
 */
export const toFabricValue = (value, width, height) => {
  if (isGradientConfig(value)) {
    return toFabricGradient(value, width || 100, height || 100);
  }
  return value; // string (hex or 'transparent')
};

/**
 * 역변환: string 그대로, fabric.Gradient → GradientConfig
 */
export const fromFabricValue = (fabricValue, width, height) => {
  if (isFabricGradient(fabricValue)) {
    return fromFabricGradient(fabricValue, width || 100, height || 100);
  }
  return fabricValue; // string
};

/**
 * GradientConfig → CSS gradient 문자열 (프리뷰용)
 */
export const toCssGradient = (config) => {
  const stops = [...config.colorStops]
    .sort((a, b) => a.offset - b.offset)
    .map((s) => {
      const color = s.opacity < 1 ? hexToRgba(s.color, s.opacity) : s.color;
      return `${color} ${Math.round(s.offset * 100)}%`;
    })
    .join(', ');

  if (config.type === 'radial') {
    return `radial-gradient(circle, ${stops})`;
  }
  return `linear-gradient(${((config.angle ?? 0) + 90) % 360}deg, ${stops})`;
};

/**
 * 기본 gradient 생성
 */
export const createDefaultGradient = (baseColor = '#3b82f6') => ({
  type: 'linear',
  angle: 0,
  colorStops: [
    { offset: 0, color: baseColor, opacity: 1 },
    { offset: 1, color: '#ffffff', opacity: 1 },
  ],
});

/**
 * 프리뷰 버튼용 표시값
 */
export const getDisplayColor = (value) => {
  if (isGradientConfig(value)) {
    return toCssGradient(value);
  }
  return value;
};
